import { useMemo, useState, useRef, useEffect } from 'react'
import { UserContributionDto } from '@/shared/api/users'
import { format, parseISO, startOfWeek, addDays, eachWeekOfInterval, subDays } from 'date-fns'

interface ContributionGraphProps {
  contributions: UserContributionDto[]
}

interface ContributionCell {
  date: string
  count: number
  contributions: UserContributionDto['contributions']
}

// Get color intensity based on contribution count using SitRepp brand colors
// Uses adventure (blue) scale for activity, earth (olive) for empty
function getColorClass(count: number): string {
  if (count === 0) {
    return 'bg-earth-100 dark:bg-earth-900 border border-earth-200 dark:border-earth-800'
  } else if (count >= 1 && count <= 2) {
    return 'bg-adventure-200 dark:bg-adventure-800 border border-adventure-300 dark:border-adventure-700'
  } else if (count >= 3 && count <= 4) {
    return 'bg-adventure-400 dark:bg-adventure-700 border border-adventure-500 dark:border-adventure-600'
  } else if (count >= 5 && count <= 7) {
    return 'bg-adventure-600 dark:bg-adventure-600 border border-adventure-700 dark:border-adventure-500'
  } else {
    return 'bg-adventure-800 dark:bg-adventure-500 border border-adventure-900 dark:border-adventure-400'
  }
}

// Format date for tooltip
function formatDate(dateString: string): string {
  try {
    return format(parseISO(dateString), 'MMM d, yyyy')
  } catch {
    return dateString
  }
}

// Format contribution breakdown
function formatContributions(contribs: UserContributionDto['contributions']): string {
  const parts: string[] = []
  if (contribs.trips > 0) parts.push(`${contribs.trips} trip${contribs.trips !== 1 ? 's' : ''}`)
  if (contribs.gearAdded > 0) parts.push(`${contribs.gearAdded} gear added`)
  if (contribs.gearClaimed > 0) parts.push(`${contribs.gearClaimed} gear claimed`)
  if (contribs.meals > 0) parts.push(`${contribs.meals} meal${contribs.meals !== 1 ? 's' : ''}`)
  if (contribs.photos > 0) parts.push(`${contribs.photos} photo${contribs.photos !== 1 ? 's' : ''}`)
  return parts.join(', ') || 'No contributions'
}

export function ContributionGraph({ contributions }: ContributionGraphProps) {
  const [hoveredCell, setHoveredCell] = useState<ContributionCell | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Calculate summary stats
  const totalContributions = useMemo(() => {
    return contributions.reduce((sum, contrib) => sum + contrib.count, 0)
  }, [contributions])

  const activeDays = useMemo(() => {
    return contributions.filter(contrib => contrib.count > 0).length
  }, [contributions])

  const longestStreak = useMemo(() => {
    let currentStreak = 0
    let maxStreak = 0
    contributions.forEach(contrib => {
      if (contrib.count > 0) {
        currentStreak++
        maxStreak = Math.max(maxStreak, currentStreak)
      } else {
        currentStreak = 0
      }
    })
    return maxStreak
  }, [contributions])

  // Create a map of contributions by date for quick lookup
  const contributionMap = useMemo(() => {
    const map = new Map<string, UserContributionDto>()
    contributions.forEach(contrib => {
      map.set(contrib.date, contrib)
    })
    return map
  }, [contributions])

  // Generate grid data (last 365 days, organized by weeks)
  const gridData = useMemo(() => {
    const today = new Date()
    const oneYearAgo = subDays(today, 365)
    const startDate = startOfWeek(oneYearAgo, { weekStartsOn: 0 }) // Sunday
    
    // Generate all weeks
    const weeks = eachWeekOfInterval(
      { start: startDate, end: today },
      { weekStartsOn: 0 }
    )

    // Generate grid: each week has 7 days (Sunday to Saturday)
    const grid: (ContributionCell | null)[][] = weeks.map(weekStart => {
      return Array.from({ length: 7 }, (_, dayIndex) => {
        const cellDate = addDays(weekStart, dayIndex)
        const dateString = format(cellDate, 'yyyy-MM-dd')
        
        // Only include dates within the last 365 days
        if (cellDate > today || cellDate < oneYearAgo) {
          return null
        }

        const contrib = contributionMap.get(dateString)
        return {
          date: dateString,
          count: contrib?.count || 0,
          contributions: contrib?.contributions || {
            trips: 0,
            gearAdded: 0,
            gearClaimed: 0,
            meals: 0,
            photos: 0
          }
        }
      })
    })

    return grid
  }, [contributionMap])

  // Auto-scroll to the end (right side) when component loads or gridData changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          // Scroll to the maximum scroll position (right side)
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth
        }
      })
    }
  }, [gridData])

  const handleCellMouseEnter = (cell: ContributionCell, event: React.MouseEvent<HTMLDivElement>) => {
    setHoveredCell(cell)
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    })
  }

  const handleCellMouseLeave = () => {
    setHoveredCell(null)
  }

  // Calculate month label positions - find which week contains the 1st of each month
  const monthLabels = useMemo(() => {
    const labels = new Map<number, { month: string; weekIndex: number }>()
    const seenMonths = new Set<string>()
    
    gridData.forEach((week, weekIndex) => {
      // Check each day in the week to see if it's the 1st of a month
      week.forEach((cell) => {
        if (cell) {
          const cellDate = parseISO(cell.date)
          const monthKey = `${cellDate.getFullYear()}-${cellDate.getMonth()}`
          
          // If this is the 1st of the month and we haven't seen this month yet
          if (cellDate.getDate() === 1 && !seenMonths.has(monthKey)) {
            seenMonths.add(monthKey)
            labels.set(weekIndex, {
              month: format(cellDate, 'MMM'),
              weekIndex
            })
          }
        }
      })
    })
    
    return labels
  }, [gridData])

  // Get day labels (S, M, T, W, T, F, S)
  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-earth-200 dark:border-earth-800">
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-bold text-adventure-600 dark:text-adventure-400 mb-0.5 sm:mb-1">
              {totalContributions}
            </div>
            <div className="text-xs sm:text-sm text-earth-600 dark:text-earth-400">Total</div>
          </div>
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-bold text-adventure-600 dark:text-adventure-400 mb-0.5 sm:mb-1">
              {activeDays}
            </div>
            <div className="text-xs sm:text-sm text-earth-600 dark:text-earth-400">Active Days</div>
          </div>
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-bold text-adventure-600 dark:text-adventure-400 mb-0.5 sm:mb-1">
              {longestStreak}
            </div>
            <div className="text-xs sm:text-sm text-earth-600 dark:text-earth-400">Streak</div>
          </div>
        </div>

        {totalContributions === 0 && (
          <p className="text-xs text-earth-600 dark:text-earth-400 text-center -mt-2 mb-4 sm:mb-6">
            Your activity streak starts with your first trip.
          </p>
        )}

        {/* Scrollable container for mobile */}
        <div ref={scrollContainerRef} className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="inline-block min-w-full">
            <div className="flex gap-1 sm:gap-1.5 w-full min-w-max">
              {/* Spacer for day labels column */}
              <div className="flex-shrink-0 mr-2 sm:mr-3" style={{ width: '16px' }} />
              
              {/* Month labels row - aligned with grid columns using same flex layout */}
              <div className="flex gap-1 sm:gap-1.5 flex-1 min-w-0 mb-2 sm:mb-3">
                {gridData.map((week, weekIndex) => {
                  const monthLabel = monthLabels.get(weekIndex)
                  
                  return (
                    <div
                      key={`month-${weekIndex}`}
                      className="flex-1 text-[10px] sm:text-xs font-medium text-earth-600 dark:text-earth-400 min-w-[10px]"
                    >
                      {monthLabel ? monthLabel.month : ''}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex gap-1 sm:gap-1.5 w-full min-w-max">
              {/* Day labels column */}
              <div className="flex flex-col gap-1 sm:gap-1.5 mr-2 sm:mr-3 flex-shrink-0">
                {dayLabels.map((day, index) => (
                  <div
                    key={`day-label-${index}`}
                    className="text-[10px] sm:text-xs font-medium text-earth-600 dark:text-earth-400 text-center flex items-center justify-center"
                    style={{ width: '16px', height: '14px' }}
                  >
                    {index % 2 === 0 ? day : ''}
                  </div>
                ))}
              </div>

              {/* Contribution grid */}
              <div className="flex gap-1 sm:gap-1.5 flex-1 min-w-0">
                {gridData.map((week, weekIndex) => (
                  <div key={`week-${weekIndex}`} className="flex flex-col gap-1 sm:gap-1.5 flex-1 min-w-[10px]">
                    {week.map((cell, dayIndex) => {
                      if (!cell) {
                        return (
                          <div
                            key={`empty-${weekIndex}-${dayIndex}`}
                            className="w-full aspect-square min-w-[10px]"
                          />
                        )
                      }

                      return (
                        <div
                          key={`cell-${cell.date}`}
                          className={`w-full aspect-square rounded sm:rounded-md cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-md hover:ring-2 hover:ring-adventure-400 dark:hover:ring-adventure-500 hover:ring-offset-1 hover:z-10 relative min-w-[10px] ${getColorClass(cell.count)}`}
                          onMouseEnter={(e) => handleCellMouseEnter(cell, e)}
                          onMouseLeave={handleCellMouseLeave}
                          title={`${formatDate(cell.date)}: ${cell.count} contribution${cell.count !== 1 ? 's' : ''}`}
                        />
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-earth-200 dark:border-earth-800 flex-wrap">
          <span className="text-[10px] sm:text-xs font-medium text-earth-600 dark:text-earth-400">Less</span>
          <div className="flex gap-1 sm:gap-1.5">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded sm:rounded-md border border-earth-200 dark:border-earth-800 bg-earth-100 dark:bg-earth-900" />
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded sm:rounded-md border border-adventure-300 dark:border-adventure-700 bg-adventure-200 dark:bg-adventure-800" />
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded sm:rounded-md border border-adventure-500 dark:border-adventure-600 bg-adventure-400 dark:bg-adventure-700" />
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded sm:rounded-md border border-adventure-700 dark:border-adventure-500 bg-adventure-600 dark:bg-adventure-600" />
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded sm:rounded-md border border-adventure-900 dark:border-adventure-400 bg-adventure-800 dark:bg-adventure-500" />
          </div>
          <span className="text-[10px] sm:text-xs font-medium text-earth-600 dark:text-earth-400">More</span>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="fixed z-50 px-4 py-3 text-sm bg-adventure-600 dark:bg-adventure-700 text-white rounded-lg shadow-xl pointer-events-none border border-adventure-700 dark:border-adventure-600"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            marginTop: '-8px'
          }}
        >
          <div className="font-semibold mb-1.5 text-adventure-50">
            {formatDate(hoveredCell.date)}
          </div>
          <div className="text-xs text-adventure-100 mb-1">
            {hoveredCell.count} contribution{hoveredCell.count !== 1 ? 's' : ''}
          </div>
          {hoveredCell.count > 0 && (
            <div className="text-xs text-adventure-200 mt-2 pt-2 border-t border-adventure-500 dark:border-adventure-600">
              {formatContributions(hoveredCell.contributions)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

